<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import ConversationBox from 'dashboard/components/widgets/conversation/ConversationBox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import WhatsappTemplates from 'dashboard/components/widgets/conversation/WhatsappTemplates/Modal.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  conversation: {
    type: Object,
    default: () => ({}),
  },
  mapping: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();

// Contato do negócio: todas as conversas dele (de qualquer caixa) aparecem
// como abas aqui; a exibida no ConversationBox é a `displayedId`. Trocar de
// conversa NÃO troca o negócio — o card continua o mesmo.
const contactId = computed(
  () => props.conversation?.meta?.sender?.id || props.conversation?.contact_id
);
const displayedId = ref(Number(props.conversation?.id));

const displayedConversation = computed(
  () =>
    store.getters.getConversationById(displayedId.value) || props.conversation
);

const contactConversations = computed(() => {
  if (!contactId.value) return [];
  return (
    store.getters['contactConversations/getAllConversationsByContactId'](
      contactId.value
    ) || []
  );
});

const inboxLabel = conv => {
  const inboxId = conv.inboxId || conv.inbox_id;
  const inbox = store.getters['inboxes/getInbox'](inboxId);
  return inbox?.name || `#${inboxId}`;
};

const statusDotClass = status => {
  switch (status) {
    case 'open':
      return 'bg-n-teal-9';
    case 'pending':
      return 'bg-n-amber-9';
    case 'snoozed':
      return 'bg-n-blue-9';
    default:
      return 'bg-n-slate-8';
  }
};

const fetchContactConversations = async () => {
  if (!contactId.value) return;
  await store.dispatch('contactConversations/get', contactId.value);
};

// Ativa uma conversa como "selecionada" para o ConversationBox (mesmo fluxo do
// modal de conversa), garantindo que ela esteja no store antes.
const activate = async id => {
  const cid = Number(id ?? displayedId.value);
  if (!cid) return;
  let conv = store.getters.getConversationById(cid);
  if (!conv) {
    await store.dispatch('getConversation', cid);
    conv = store.getters.getConversationById(cid);
  }
  if (!conv) return;

  await store.dispatch('setActiveChat', { data: conv });
  store.commit('SET_CURRENT_CHAT_WINDOW', conv);

  if (conv.inbox_id) {
    await store.dispatch('inboxAssignableAgents/fetch', [conv.inbox_id]);
    await store.dispatch('inboxes/get');
  }
};

const selectConversation = async conv => {
  const id = Number(conv.id);
  if (id === displayedId.value) return;
  displayedId.value = id;
  await activate(id);
};

// Handler do <select> (recebe o id; com muitos números os chips estouravam a
// largura e escondiam conversas — seletor mostra todas).
const onSelectConversation = id => {
  const conv = contactConversations.value.find(
    c => Number(c.id) === Number(id)
  );
  if (conv) selectConversation(conv);
};

// Ao fechar o compositor: se surgiu uma conversa nova, passa a exibi-la.
const onComposeClose = async () => {
  const before = contactConversations.value.length;
  await fetchContactConversations();
  const list = contactConversations.value;
  if (list.length > before && list.length) {
    const newest = [...list].sort((a, b) => Number(b.id) - Number(a.id))[0];
    if (newest) await selectConversation(newest);
  }
};

// Template de WhatsApp: reaproveita o mesmo modal do ReplyBox pra reengajar
// o contato (ex.: fora da janela de 24h no Meta Cloud) sem sair do negócio.
// Só aparece em inbox WhatsApp COM template aprovado (some no WAHA, que não usa
// template).
const inboxForConversation = computed(() =>
  store.getters['inboxes/getInbox'](displayedConversation.value?.inbox_id)
);
const approvedTemplates = computed(
  () =>
    store.getters['inboxes/getFilteredWhatsAppTemplates'](
      displayedConversation.value?.inbox_id
    ) || []
);
const canSendTemplate = computed(
  () =>
    inboxForConversation.value?.channel_type === INBOX_TYPES.WHATSAPP &&
    approvedTemplates.value.length > 0
);
const showTemplateModal = ref(false);
const onSendTemplate = payload => {
  store.dispatch('createPendingMessageAndSend', {
    conversationId: displayedConversation.value.id,
    ...payload,
  });
  showTemplateModal.value = false;
};

// "Precisa de atenção" band — the next scheduled/confirmed appointment for
// this conversation (mirrors KanbanCard's appointmentDateTime rule, the only
// ready "next activity" data source today). Renders nothing when there is
// none instead of inventing a generic reminder.
// Só conta o que ainda vai acontecer: um agendamento que segue "scheduled"
// mas cujo horário já passou (ninguém marcou como realizado/falta) não é
// "próximo" — sem esse corte a faixa anunciava reunião de dias atrás.
const appointmentStart = apt =>
  new Date(apt.appointment_date || apt.scheduled_at).getTime();

const nextAppointment = computed(() => {
  const appointments = props.conversation?.conversation_appointments || [];
  const now = Date.now();
  const upcoming = appointments
    .filter(apt => apt.status === 'scheduled' || apt.status === 'confirmed')
    .filter(apt => appointmentStart(apt) >= now)
    .sort((a, b) => appointmentStart(a) - appointmentStart(b));
  return upcoming[0] || null;
});

const nextAppointmentLabel = computed(() => {
  const appointment = nextAppointment.value;
  if (!appointment) return '';
  const date = new Date(
    appointment.appointment_date || appointment.scheduled_at
  );
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
});

// Second attention signal: the deal's expected close date (from `mapping`,
// same field DealAbout reads/edits, with the same conversation fallback
// DealAbout's `expectedCloseDate` uses) has passed — mirrors
// DealDetailsSidebar's `isOverdue` rule. Takes priority over the next
// appointment since a missed close date is more urgent.
const isCloseDateOverdue = computed(() => {
  const expectedCloseDate =
    props.mapping?.expected_close_date ||
    props.conversation?.expected_close_date;
  if (!expectedCloseDate) return false;
  const date = new Date(expectedCloseDate);
  if (Number.isNaN(date.getTime())) return false;
  return date < new Date();
});

const attentionMessage = computed(() => {
  if (isCloseDateOverdue.value) {
    return t('DEAL_RECORD.CONVERSATION.OVERDUE_CLOSE_DATE');
  }
  if (nextAppointmentLabel.value) {
    return t('DEAL_RECORD.CONVERSATION.NEXT_APPOINTMENT', {
      when: nextAppointmentLabel.value,
    });
  }
  return '';
});

// Screen-level active chat activation — mirrors ConversationModal's
// `loadConversation()` sequence so the reused components that read the
// Vuex `getSelectedChat`/`selectedChatId` getters (ContactInfo and
// CustomAttributes in DealAbout, ConversationBox below) all point at this
// deal's conversation instead of a stale one from a previous screen.
// `useDealRecord.load()` already guarantees the conversation is in the
// store (dispatching `getConversation` if missing), so this only needs to
// read it back via `getConversationById` (the action itself does not
// return the conversation) and activate it — no extra fetch here.
// Spinner enquanto a conversa "ativa" (fetch + setActiveChat): sem ele a área
// da conversa ficava em branco por segundos e parecia travado.
const isActivating = ref(true);

onMounted(async () => {
  try {
    await activate(displayedId.value);
  } finally {
    isActivating.value = false;
  }
  // Carrega o contato (para o compositor pré-selecionar) e a lista de conversas.
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
    fetchContactConversations();
  }
  store.dispatch('inboxes/get');
});

onBeforeUnmount(() => {
  if (props.conversation?.id) {
    store.dispatch('clearSelectedState');
  }
});
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 gap-3 p-4">
    <div
      v-if="attentionMessage"
      class="flex items-center gap-2.5 px-3 py-2 text-sm border rounded-lg shrink-0 bg-gradient-to-r"
      :class="
        isCloseDateOverdue
          ? 'from-n-ruby-3 to-transparent border-n-ruby-6 text-n-ruby-11'
          : 'from-n-amber-3 to-transparent border-n-amber-6 text-n-amber-11'
      "
    >
      <span
        class="shrink-0 rounded-full size-2"
        :class="isCloseDateOverdue ? 'bg-n-ruby-9' : 'bg-n-amber-9'"
      />
      <Icon
        :icon="
          isCloseDateOverdue
            ? 'i-lucide-alert-triangle'
            : 'i-lucide-calendar-clock'
        "
        class="shrink-0 size-4"
      />
      <span class="font-medium">{{ attentionMessage }}</span>
    </div>

    <!-- Conversas do contato (todas as caixas) + iniciar por outra caixa -->
    <div class="flex items-center gap-2 shrink-0">
      <div class="flex items-center gap-2 min-w-0">
        <span class="text-xs font-medium text-n-slate-10 shrink-0">
          {{ t('DEAL_RECORD.CONVERSATION.CHANNELS') }}
        </span>
        <div v-if="contactConversations.length" class="relative shrink-0">
          <span
            class="absolute top-1/2 -translate-y-1/2 rounded-full size-1.5 pointer-events-none ltr:left-2.5 rtl:right-2.5"
            :class="statusDotClass(displayedConversation.status)"
          />
          <select
            :value="displayedId"
            class="reset-base appearance-none max-w-56 truncate cursor-pointer rounded-full border border-n-weak bg-transparent py-1 text-xs font-medium text-n-slate-12 outline-none transition-colors hover:border-n-slate-7 hover:bg-n-alpha-1 focus-visible:outline-n-brand ltr:pl-7 ltr:pr-7 rtl:pr-7 rtl:pl-7"
            @change="onSelectConversation($event.target.value)"
          >
            <option
              v-for="conv in contactConversations"
              :key="conv.id"
              :value="Number(conv.id)"
            >
              {{ inboxLabel(conv) }}
            </option>
          </select>
          <span
            class="absolute top-1/2 -translate-y-1/2 pointer-events-none i-lucide-chevron-down size-3.5 text-n-slate-10 ltr:right-2 rtl:left-2"
          />
        </div>
      </div>

      <button
        v-if="canSendTemplate"
        type="button"
        class="flex items-center gap-1 px-2.5 py-1 ml-auto text-xs font-medium transition-colors border rounded-full shrink-0 border-n-teal-6 text-n-teal-11 hover:bg-n-teal-3"
        :title="t('DEAL_RECORD.CONVERSATION.SEND_TEMPLATE_TITLE')"
        @click="showTemplateModal = true"
      >
        <Icon icon="i-lucide-message-square-text" class="size-3.5" />
        {{ t('DEAL_RECORD.CONVERSATION.SEND_TEMPLATE') }}
      </button>

      <ComposeConversation
        v-if="contactId"
        :contact-id="String(contactId)"
        :default-inbox-id="displayedConversation.inbox_id"
        is-modal
        align-position="right"
        :class="canSendTemplate ? 'shrink-0' : 'shrink-0 ml-auto'"
        @close="onComposeClose"
      >
        <template #trigger="{ toggle }">
          <button
            type="button"
            class="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border border-dashed border-n-weak text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12 transition-colors shrink-0"
            :title="t('DEAL_RECORD.CONVERSATION.NEW_ON_INBOX_TITLE')"
            @click="toggle"
          >
            <Icon icon="i-lucide-plus" class="size-3.5" />
            {{ t('DEAL_RECORD.CONVERSATION.NEW_ON_INBOX') }}
          </button>
        </template>
      </ComposeConversation>
    </div>

    <WhatsappTemplates
      v-if="canSendTemplate"
      v-model:show="showTemplateModal"
      :inbox-id="displayedConversation.inbox_id"
      @on-send="onSendTemplate"
      @cancel="showTemplateModal = false"
    />

    <div
      class="relative flex-1 w-full min-h-0 overflow-hidden border rounded-lg border-n-weak bg-n-solid-1"
    >
      <div
        v-if="isActivating"
        class="absolute inset-0 z-10 flex items-center justify-center bg-n-solid-1 text-n-slate-11"
      >
        <Spinner />
      </div>
      <ConversationBox
        v-if="displayedConversation.id && !isActivating"
        :key="displayedConversation.id"
        :inbox-id="displayedConversation.inbox_id"
        :is-contact-panel-open="false"
        :is-on-expanded-layout="false"
        :is-inbox-view="false"
        class="h-full"
      />
    </div>
  </div>
</template>

<style scoped>
/* Respiro entre a área de mensagens (WhatsApp) e o compositor — só nesta tela.
   Alcança o elemento interno do ConversationBox compartilhado via :deep. */
:deep(.reply-box) {
  margin-top: 0.75rem;
}
</style>
